import clsx from "clsx"
import { useRef } from "react"

import Dialog from "@molecules/Dialog/Dialog"
import Popover from "@molecules/Popover/Popover"
import { useSelectContext } from "@molecules/Select/Select.context"
import useSelectAutoFocus from "@molecules/Select/Select.hooks"
import SelectListSearch from "@molecules/Select/SelectListSearch/SelectListSearch"

import styles from "./SelectListDialog.module.scss"
import { SelectListDialogProps } from "./SelectListDialog.types"

export default function SelectListDialog(props: SelectListDialogProps) {
    const { title, children, className, showSearch, searchProps, footer, ...rest } = props
    const { isOpen, searchData } = useSelectContext()
    const contentRef = useRef<HTMLDivElement>(null)

    useSelectAutoFocus(contentRef, Boolean(isOpen))

    const effectiveSearchProps =
        searchProps ??
        (searchData
            ? {
                  value: searchData.searchState.searchTerm,
                  onChange: searchData.searchState.onSearchChange,
                  isLoading: searchData.isLoading || (searchData.isFetching && !searchData.isFetchingNextPage),
              }
            : undefined)

    const isDataEmpty =
        searchData !== undefined &&
        !searchData.isLoading &&
        !searchData.isFetching &&
        !searchData.searchState.isTyping &&
        !searchData.searchState.hasSearchTerm &&
        searchData.results.length === 0

    const effectiveShowSearch = showSearch !== undefined ? showSearch : searchData ? !isDataEmpty : false

    return (
        <>
            <Popover.Overlay isActive={Boolean(isOpen)} />
            <Popover.Viewport onOpenAutoFocus={(event) => event.preventDefault()}>
                <div
                    ref={contentRef}
                    className={clsx(styles.content, className)}
                    // cmdk's Command root catches Enter anywhere inside it and dispatches a select
                    // on the currently aria-highlighted item, preventing buttons (close, footer
                    // actions) from receiving their normal Enter→click. Stop propagation for
                    // Enter on button targets so cmdk only sees Enter from inputs and items.
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && (event.target as HTMLElement).tagName === "BUTTON") {
                            event.stopPropagation()
                        }
                    }}
                    {...rest}
                >
                    <Dialog.Header.Root>
                        <Dialog.Header.Title title={title} />
                        <Dialog.Header.CloseButton />
                    </Dialog.Header.Root>
                    <Dialog.Body className={styles.body}>
                        {effectiveShowSearch && <SelectListSearch {...effectiveSearchProps} />}
                        {children}
                    </Dialog.Body>
                    {footer}
                </div>
            </Popover.Viewport>
        </>
    )
}
